import { useState } from 'react';
import {
  Card,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  Divider,
} from '@mui/material';
import {
  IoTime,
  IoTrendingUp,
  IoTrendingDown,
  IoCheckmarkCircle,
  IoClose,
  IoSettings,
  IoEye,
  IoDownload,
  IoCalendar,
  IoPersonCircle,
  IoStatsChart,
} from 'react-icons/io5';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow, format, subDays, subWeeks, subMonths } from 'date-fns';

import { transactionService } from 'src/services/transaction.service';
import { studentService } from 'src/services/student.service';
import { ruleEngineService } from 'src/services/ruleEngine.service';

interface StudentEngagementReportProps {
  teacherId: string;
  classIds?: string[];
}

interface StudentEngagementData {
  studentId: string;
  studentName: string;
  totalTransactions: number;
  responseRate: number;
  averageResponseTime: number; // in hours
  deferralsUsed: number;
  autoRulesCreated: number;
  lastActivity: string;
  engagementScore: number; // 0-100
  trends: {
    responseTimeImprovement: number;
    ruleAdoption: boolean;
    consistency: number;
  };
}

type DateRange = '7d' | '30d' | '90d';

export default function StudentEngagementReport({ teacherId, classIds }: StudentEngagementReportProps) {
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [selectedStudent, setSelectedStudent] = useState<StudentEngagementData | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Calculate date range
  const getDateRange = () => {
    const now = new Date();
    switch (dateRange) {
      case '7d':
        return { from: subDays(now, 7), to: now };
      case '30d':
        return { from: subDays(now, 30), to: now };
      case '90d':
        return { from: subDays(now, 90), to: now };
      default:
        return { from: subDays(now, 30), to: now };
    }
  };

  // Fetch engagement data
  const {
    data: engagementData = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['student-engagement-report', teacherId, classIds, dateRange],
    queryFn: async () => {
      const { from, to } = getDateRange();
      
      // For now, use mock students data
      // In a full implementation, this would query students by teacher/class
      const students = [
        {
          id: 'student1',
          user: { firstname: 'John', lastname: 'Doe' }
        },
        {
          id: 'student2', 
          user: { firstname: 'Jane', lastname: 'Smith' }
        }
      ];
      
      // Get engagement data for each student
      const engagementPromises = students.map(async (student) => {
        // Get student transactions in date range
        const { data: transactions } = await transactionService.getTransactions(1, 1000, {
          studentId: student.id,
          startDate: from,
          endDate: to,
        });

        // Get student rules
        const studentRules = await ruleEngineService.getStudentRules(student.id);
        
        // Calculate metrics
        const totalTransactions = transactions.length;
        const respondedTransactions = transactions.filter(t => 
          t.student_action && t.student_action_date
        );
        
        const responseRate = totalTransactions > 0 ? 
          (respondedTransactions.length / totalTransactions) * 100 : 0;

        // Calculate average response time
        const responseTimes = respondedTransactions
          .filter(t => t.student_action_date)
          .map(t => {
            const created = new Date(t.createdat);
            const responded = new Date(t.student_action_date!);
            return (responded.getTime() - created.getTime()) / (1000 * 60 * 60); // hours
          });

        const averageResponseTime = responseTimes.length > 0 ? 
          responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length : 0;

        const deferralsUsed = transactions.filter(t => t.deferred_count > 0).length;
        const autoRulesCreated = studentRules.length;
        
        const lastActivity = transactions.length > 0 ? 
          Math.max(...transactions.map(t => new Date(t.updatedat).getTime())) : 0;

        // Calculate engagement score (0-100)
        let engagementScore = 0;
        if (responseRate >= 80) engagementScore += 30;
        else if (responseRate >= 60) engagementScore += 20;
        else if (responseRate >= 40) engagementScore += 10;

        if (averageResponseTime <= 24) engagementScore += 25; // Quick responses
        else if (averageResponseTime <= 48) engagementScore += 15;
        else if (averageResponseTime <= 72) engagementScore += 5;

        if (autoRulesCreated >= 3) engagementScore += 25; // Rule adoption
        else if (autoRulesCreated >= 1) engagementScore += 15;

        if (deferralsUsed <= 2) engagementScore += 10; // Responsible deferrals
        else if (deferralsUsed <= 5) engagementScore += 5;

        if (totalTransactions >= 5) engagementScore += 10; // Activity level

        const studentData: StudentEngagementData = {
          studentId: student.id,
          studentName: `${student.user.firstname} ${student.user.lastname}`,
          totalTransactions,
          responseRate,
          averageResponseTime,
          deferralsUsed,
          autoRulesCreated,
          lastActivity: lastActivity ? new Date(lastActivity).toISOString() : '',
          engagementScore: Math.min(engagementScore, 100),
          trends: {
            responseTimeImprovement: Math.random() * 20 - 10, // Mock data
            ruleAdoption: autoRulesCreated > 0,
            consistency: responseRate,
          },
        };

        return studentData;
      });

      const results = await Promise.all(engagementPromises);
      return results.sort((a, b) => b.engagementScore - a.engagementScore);
    },
    enabled: !!teacherId,
  });

  // Calculate class averages
  const classAverages = engagementData.length > 0 ? {
    responseRate: engagementData.reduce((sum, student) => sum + student.responseRate, 0) / engagementData.length,
    responseTime: engagementData.reduce((sum, student) => sum + student.averageResponseTime, 0) / engagementData.length,
    engagementScore: engagementData.reduce((sum, student) => sum + student.engagementScore, 0) / engagementData.length,
    ruleAdoption: (engagementData.filter(student => student.autoRulesCreated > 0).length / engagementData.length) * 100,
  } : null;

  const getEngagementLevel = (score: number): { level: string; color: 'success' | 'warning' | 'error' } => {
    if (score >= 80) return { level: 'High', color: 'success' };
    if (score >= 60) return { level: 'Medium', color: 'warning' };
    return { level: 'Low', color: 'error' };
  };

  const getResponseTimeColor = (hours: number) => {
    if (hours <= 24) return 'success.main';
    if (hours <= 48) return 'warning.main';
    return 'error.main';
  };

  const handleViewDetails = (student: StudentEngagementData) => {
    setSelectedStudent(student);
    setIsDetailModalOpen(true);
  };

  if (error) {
    return (
      <Alert severity="error">
        <AlertTitle>Error Loading Engagement Data</AlertTitle>
        Unable to load student engagement report. Please try refreshing.
      </Alert>
    );
  }

  return (
    <Card sx={{ borderRadius: 3 }}>
      <Box p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight="bold">
            Student Engagement Report
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as DateRange)}
                label="Period"
              >
                <MenuItem value="7d">Last 7 days</MenuItem>
                <MenuItem value="30d">Last 30 days</MenuItem>
                <MenuItem value="90d">Last 90 days</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" startIcon={<IoDownload />} size="small">
              Export
            </Button>
          </Box>
        </Box>

        {/* Class Summary */}
        {classAverages && (
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <IoStatsChart />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{Math.round(classAverages.engagementScore)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Avg Engagement Score
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 2, bgcolor: 'success.50', borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <IoCheckmarkCircle />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{Math.round(classAverages.responseRate)}%</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Avg Response Rate
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 2, bgcolor: 'info.50', borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'info.main' }}>
                    <IoTime />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{Math.round(classAverages.responseTime)}h</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Avg Response Time
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: 'warning.main' }}>
                    <IoSettings />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{Math.round(classAverages.ruleAdoption)}%</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Rule Adoption Rate
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Students Table */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            Loading engagement data...
          </Box>
        ) : engagementData.length === 0 ? (
          <Box textAlign="center" py={4}>
            <IoPersonCircle size={48} color="#757575" />
            <Typography variant="h6" mt={2}>No Data Available</Typography>
            <Typography variant="body2" color="text.secondary">
              No student engagement data found for the selected period.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Engagement Score</TableCell>
                  <TableCell>Response Rate</TableCell>
                  <TableCell>Avg Response Time</TableCell>
                  <TableCell>Rules Created</TableCell>
                  <TableCell>Last Activity</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {engagementData.map((student) => {
                  const engagement = getEngagementLevel(student.engagementScore);
                  return (
                    <TableRow key={student.studentId} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                            {student.studentName.split(' ').map(n => n[0]).join('')}
                          </Avatar>
                          <Typography variant="body2">{student.studentName}</Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box sx={{ width: 60 }}>
                            <LinearProgress
                              variant="determinate"
                              value={student.engagementScore}
                              color={engagement.color}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </Box>
                          <Typography variant="body2">{student.engagementScore}</Typography>
                          <Chip
                            label={engagement.level}
                            size="small"
                            color={engagement.color}
                          />
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2">
                            {Math.round(student.responseRate)}%
                          </Typography>
                          {student.trends.responseTimeImprovement > 0 ? (
                            <IoTrendingUp color="#4caf50" size={16} />
                          ) : student.trends.responseTimeImprovement < 0 ? (
                            <IoTrendingDown color="#f44336" size={16} />
                          ) : null}
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          color={getResponseTimeColor(student.averageResponseTime)}
                        >
                          {Math.round(student.averageResponseTime)}h
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2">{student.autoRulesCreated}</Typography>
                          {student.trends.ruleAdoption && (
                            <Chip label="Adopted" size="small" color="success" />
                          )}
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {student.lastActivity ? 
                            formatDistanceToNow(new Date(student.lastActivity), { addSuffix: true }) :
                            'No activity'
                          }
                        </Typography>
                      </TableCell>
                      
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<IoEye />}
                          onClick={() => handleViewDetails(student)}
                        >
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Student Detail Modal */}
      <Dialog
        open={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Typography variant="h6">
            {selectedStudent?.studentName} - Detailed Engagement
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          {selectedStudent && (
            <Stack spacing={3}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Engagement Score
                    </Typography>
                    <Typography variant="h4" color="primary.main">
                      {selectedStudent.engagementScore}/100
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Transactions
                    </Typography>
                    <Typography variant="h4">
                      {selectedStudent.totalTransactions}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Divider />

              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="h6" color="success.main">
                      {Math.round(selectedStudent.responseRate)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Response Rate
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="h6" color="info.main">
                      {Math.round(selectedStudent.averageResponseTime)}h
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Avg Response Time
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="h6" color="warning.main">
                      {selectedStudent.autoRulesCreated}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Rules Created
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Alert severity="info">
                <AlertTitle>Engagement Insights</AlertTitle>
                <Typography variant="body2">
                  This student shows {getEngagementLevel(selectedStudent.engagementScore).level.toLowerCase()} engagement
                  with the transaction system. 
                  {selectedStudent.autoRulesCreated > 0 && ' They have adopted automation rules, showing advanced usage.'}
                  {selectedStudent.responseRate > 80 && ' Excellent response rate indicates high responsibility.'}
                  {selectedStudent.averageResponseTime <= 24 && ' Quick response times show active participation.'}
                </Typography>
              </Alert>

              <Box display="flex" justifyContent="flex-end">
                <Button onClick={() => setIsDetailModalOpen(false)}>
                  Close
                </Button>
              </Box>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}